import {useMemo} from 'react';
import {
  ChartData,
  Position,
  PositionEventType,
} from '@/providers/overview/OverviewProvider';
import {formatEther, formatUnits, getAddress} from 'ethers';
import {addPositionChartData} from '@/providers/overview/addPositionChartData';
import {
  AccountState,
  useAccountData,
} from '@/providers/account/AccountProvider';
import {ETH_ADDRESS} from '@/util/chains';

export const useTokenPositions = (chartData: ChartData) => {
  const accountData = useAccountData();
  return useMemo(() => {
    return parseTokenData(chartData, accountData);
  }, [chartData, accountData.blockNumber]);
};

function parseTokenData(chartData: ChartData, accountData: AccountState) {
  if (
    !chartData['kei-finance'] ||
    !accountData.address ||
    !accountData.blockNumber
  ) {
    return [];
  }

  const tokenBalances: Record<string, Position> = {};

  accountData.balances?.forEach(
    ({
      contract_ticker_symbol,
      contract_address,
      contract_name,
      image,
      balance,
      contract_decimals,
    }) => {
      const chartDataItem = chartData[contract_address.toLowerCase()];

      if (!chartDataItem) {
        console.warn(`No chart data found for ${contract_address}`);
        return;
      }

      if (!tokenBalances[contract_address]) {
        tokenBalances[contract_address] = {
          id: contract_address,
          type: 'Token',
          symbol: contract_ticker_symbol,
          image: image.small,
          name: contract_name,
          balances: [],
          events: [],
          decimals: contract_decimals,
          color: chartDataItem?.color,
          chartData: chartDataItem?.timeSeries.concat() || [],
          assets: [],
          growth: 0,
          value: 0,
          balance: '0',
        };
      }

      const tokenPrice = chartDataItem?.getPrice(undefined, 0) ?? 0;

      tokenBalances[contract_address].balances.push({
        value: +formatUnits(balance, contract_decimals) * tokenPrice,
        balance,
        timestamp: 0,
        blockNumber: accountData.blockNumber!.toString(),
        getValueAt(timestamp?: number): number {
          return (
            +formatUnits(balance, contract_decimals) *
            (chartDataItem?.getPrice(timestamp, 0) ?? 0)
          );
        },
      });
    }
  );

  const txs = (accountData.txs || [])
    .concat(accountData.internalTxs || [])
    .sort((a, b) => {
      return +b.blockNumber - +a.blockNumber;
    });

  const ethPosition = tokenBalances[ETH_ADDRESS];
  if (ethPosition && accountData.address) {
    const ethData = chartData[ETH_ADDRESS];
    let currentBalance = BigInt(
      ethPosition.balances[ethPosition.balances.length - 1].balance
    );
    let newBalance = currentBalance;
    txs
      .concat()
      .sort((a, b) => {
        return +b.blockNumber - +a.blockNumber;
      })
      .forEach(tx => {
        const timestamp = +tx.timeStamp;
        const value = BigInt(tx.value);

        if (
          getAddress(tx.to) === getAddress(accountData.address!) &&
          value > 0n
        ) {
          newBalance -= value;
          ethPosition.events.unshift({
            type: PositionEventType.RECEIVE,
            amount: tx.value,
            symbol: 'ETH',
            value: +formatEther(tx.value) * ethData.getPrice(timestamp, 0),
            timestamp,
            decimals: 18,
            tx: tx.hash,
          });
        }

        if (getAddress(tx.from) === getAddress(accountData.address!)) {
          if (value > 0n) {
            newBalance += value;
            ethPosition.events.unshift({
              type: PositionEventType.SEND,
              amount: tx.value,
              symbol: 'ETH',
              value: +formatEther(tx.value) * ethData.getPrice(timestamp, 0),
              timestamp,
              decimals: 18,
              tx: tx.hash,
            });
          }

          const gasAmount = BigInt(tx.gasUsed) * BigInt(tx.gasPrice);
          ethPosition.events.unshift({
            type: PositionEventType.GAS,
            amount: gasAmount.toString(),
            symbol: 'ETH',
            value: +formatEther(gasAmount) * ethData.getPrice(timestamp, 0),
            timestamp,
            decimals: 18,
            tx: tx.hash,
          });

          newBalance += gasAmount;
        }

        if (newBalance !== currentBalance) {
          ethPosition.balances[0].timestamp = timestamp;
          ethPosition.balances.unshift({
            value: +formatEther(newBalance) * ethData.getPrice(timestamp, 0),
            balance: newBalance.toString(),
            blockNumber: tx.blockNumber,
            timestamp: 0,
            getValueAt(timestamp?: number): number {
              return (
                +formatEther(this.balance) * ethData.getPrice(timestamp, 0)
              );
            },
          });
          currentBalance = newBalance;
        }
      });
  }

  accountData.transfers
    ?.concat()
    .sort((a, b) => {
      return +b.blockNumber - +a.blockNumber;
    })
    .forEach(transfer => {
      if (!transfer.contractAddress) {
        return;
      }

      const position = tokenBalances[transfer.contractAddress.toLowerCase()];

      if (!position) {
        return;
      }

      let newBalance = BigInt(position.balances[0].balance);
      const amount = BigInt(transfer.value);

      if (amount === 0n) {
        return;
      }

      const data = chartData[transfer.contractAddress.toLowerCase()];
      const timestamp = Math.floor(
        new Date(transfer.blockTimestamp).getTime() / 1000
      );
      const decimals = +transfer.contractDecimals;

      if (
        getAddress(accountData.address!) === getAddress(transfer.fromAddress)
      ) {
        newBalance += amount;

        position.events.unshift({
          type: PositionEventType.SEND,
          amount: amount.toString(),
          symbol: transfer.contractSymbol,
          value: +formatUnits(amount, decimals) * data.getPrice(timestamp, 0),
          timestamp,
          decimals,
          tx: transfer.transactionHash,
        });
      } else {
        newBalance -= amount;

        position.events.unshift({
          type: PositionEventType.RECEIVE,
          amount: amount.toString(),
          symbol: transfer.contractSymbol,
          value: +formatUnits(amount, decimals) * data.getPrice(timestamp, 0),
          timestamp,
          decimals,
          tx: transfer.transactionHash,
        });
      }

      tokenBalances[transfer.contractAddress].balances[0].timestamp = timestamp;
      tokenBalances[transfer.contractAddress].balances.unshift({
        value: +formatUnits(newBalance, decimals) * data.getPrice(timestamp, 0),
        balance: newBalance.toString(),
        timestamp: 0,
        blockNumber: transfer.blockNumber.toString(),
        getValueAt(timestamp?: number): number {
          return (
            +formatUnits(this.balance, decimals) * data.getPrice(timestamp, 0)
          );
        },
      });
    });

  Object.entries(tokenBalances).forEach(([key, position]) => {
    try {
      addPositionChartData(position);
    } catch (e) {
      console.error(e);
      if (position.symbol.toLowerCase() !== 'kei') {
        delete tokenBalances[key];
      }
    }
  });

  return Object.values(tokenBalances);
}
